/**
 * MEDIA_ELEMENT_CROSS_HOST_URL_AUDIT_AND_FIX_V1 — pure core of
 * `resolveSaasMediaUrl` (see `api.ts`), split into its own zero-import
 * module so it can be unit-tested directly with a plain Node + esbuild
 * transform (this repo has no vitest/jest runner) without dragging in
 * `import.meta.env` or any Vite-only global.
 *
 * The problem this generalizes: a native browser media loader — `<img
 * src>`, `<audio src>`, `<video src>`, `poster`, a download `<a href>` —
 * never goes through axios/fetch's own `baseURL`. A bare backend-relative
 * `"/api/..."` string handed directly to one of those only ever worked by
 * accident, when the frontend and this API happened to share an origin
 * (local dev's Vite proxy). Once they live on different origins (Preview:
 * `app-preview.vistoera.com` vs `api-preview.vistoera.com`; Production is
 * the same shape), that relative path resolves against the *page's* own
 * origin instead — the static frontend host swallows it with its own SPA
 * fallback (HTTP 200, `text/html`) instead of ever reaching the API, and
 * the `<audio>`/`<img>`/`<video>` element then fails to decode HTML as
 * media. See `canvasVoiceTemplatePlaybackUrl`'s fix history (Voice
 * Template playback) and `canvasAudioPlaybackUrl` (Generate Audio) for
 * two real incidents of exactly this shape.
 */

/**
 * @param pathOrUrl - anything a call site might otherwise hand straight to
 *   a native media loader: a backend-relative API path, an already-
 *   absolute `http(s)://` URL, a `blob:`/`data:` URL, or null/undefined.
 * @param apiOrigin - this API's own absolute origin (no `/api/...`
 *   suffix), e.g. `https://api-preview.vistoera.com`.
 * @returns
 *   - `null` for a null/undefined/empty input.
 *   - the input unchanged for an already-absolute `http(s)://` URL, a
 *     `blob:` URL, or a `data:` URL — never re-prefixed, so this is safe
 *     to call on a value that has already been resolved (idempotent) and
 *     safe for canonical CDN/R2/signed URLs, which must never be routed
 *     through this API's own origin.
 *   - `apiOrigin + pathOrUrl` for a backend-relative `"/api/..."` path.
 *   - the input unchanged for any other relative path (e.g. a frontend-
 *     local route) — this helper only ever rewrites a path it can
 *     positively identify as this backend's own API surface.
 */
export function resolveMediaUrlAgainstOrigin(
  pathOrUrl: string | null | undefined,
  apiOrigin: string,
): string | null {
  if (!pathOrUrl) return null
  if (/^(https?:|blob:|data:)/i.test(pathOrUrl)) return pathOrUrl
  if (pathOrUrl.startsWith('/api/')) return `${apiOrigin}${pathOrUrl}`
  return pathOrUrl
}
